/**
 * Bulk Priority Management Component (Jun 2026)
 *
 * Renders next to BulkAssignmentControls on the dashboard. Lets Admin /
 * Team Leaders flip the priority of up to 100 selected orders in one
 * action (Normal / Rush / Urgent), with a shared justification when
 * targeting Rush or Urgent.
 *
 * Decisions baked in (Ops, Jun 2026):
 *   • One shared `priority_notes` per batch when Rush/Urgent.
 *   • Downgrading to Normal does NOT wipe existing notes (server-side).
 *   • Locked / completed / cancelled orders are skipped server-side.
 *   • 100-order cap, parity with bulk-assign.
 */
import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Flag } from 'lucide-react';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { Label } from '../../ui/label';

const API = process.env.REACT_APP_BACKEND_URL;

const DISPLAY_TO_KEY = {
  'Tax Search Team Leader': 'tax_search_team_leader',
  'Title Search Team Leader': 'title_search_team_leader',
  'Admin': 'admin',
};
const nRole = (r) => DISPLAY_TO_KEY[r] || r || '';

const ALLOWED_ROLES = new Set([
  'admin',
  'team_leader',
  'tax_search_team_leader',
  'title_search_team_leader',
]);

const PRIORITY_OPTIONS = [
  { value: 'normal', label: 'Normal', icon: '🟡' },
  { value: 'rush', label: 'Rush', icon: '🔵' },
  { value: 'urgent', label: 'Urgent', icon: '🔴' },
];

/**
 * BulkPriorityControls — pill-style control + modal for bulk priority.
 */
export const BulkPriorityControls = ({
  user,
  selectedOrderIds,       // Set of selected order ids (shared with bulk-assign)
  selectedOrders,         // Admin selection Set (legacy parallel state)
  onCompleted,            // () => void — caller refreshes orders + clears selection
}) => {
  const canBulkPriority = ALLOWED_ROLES.has(nRole(user?.role));
  const isAdmin = nRole(user?.role) === 'admin';
  const selectionSet = isAdmin ? (selectedOrders || selectedOrderIds) : selectedOrderIds;
  const selectedCount = selectionSet?.size || 0;

  const [open, setOpen] = useState(false);
  const [priority, setPriority] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const needsNotes = priority === 'rush' || priority === 'urgent';
  const notesValid = !needsNotes || notes.trim().length >= 10;

  const handleOpenChange = (next) => {
    if (submitting) return;
    setOpen(next);
    if (!next) {
      setPriority('');
      setNotes('');
      setError('');
    }
  };

  const handleApply = useCallback(async () => {
    setError('');
    if (!priority) {
      setError('Select a priority.');
      return;
    }
    if (needsNotes && !notesValid) {
      setError('Justification of at least 10 characters is required for Rush/Urgent.');
      return;
    }
    if (selectedCount === 0) {
      setError('Select at least one order.');
      return;
    }
    if (selectedCount > 100) {
      setError('Maximum 100 orders per bulk operation.');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const orderIds = Array.from(selectionSet);
      const payload = {
        order_ids: orderIds,
        priority,
      };
      if (needsNotes) payload.priority_notes = notes.trim();

      const res = await axios.post(
        `${API}/api/orders/priority:batch`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { successes = [], failures = [], unchanged = [] } = res.data || {};
      const okCount = successes.length;
      const unchangedCount = unchanged.length;
      const failCount = failures.length;

      // Toast hierarchy: success > partial > all-failed
      if (okCount > 0 && failCount === 0) {
        const noChangeMsg = unchangedCount > 0
          ? ` (${unchangedCount} already at ${priority.toUpperCase()})`
          : '';
        toast.success(
          `Updated ${okCount} order${okCount !== 1 ? 's' : ''} to ${priority.toUpperCase()}${noChangeMsg}`
        );
      } else if (okCount > 0 && failCount > 0) {
        toast.warning(
          `Updated ${okCount} order${okCount !== 1 ? 's' : ''} to ${priority.toUpperCase()}; ` +
          `${failCount} skipped (locked/closed)`
        );
      } else if (okCount === 0 && unchangedCount > 0 && failCount === 0) {
        toast.info(`All ${unchangedCount} selected order${unchangedCount !== 1 ? 's were' : ' was'} already at ${priority.toUpperCase()}`);
      } else {
        toast.error(`Bulk priority update failed — ${failCount} order${failCount !== 1 ? 's' : ''} skipped`);
      }

      setOpen(false);
      setPriority('');
      setNotes('');
      onCompleted?.({ successes, failures, unchanged });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to update priority';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }, [priority, needsNotes, notesValid, notes, selectedCount, selectionSet, onCompleted]);

  if (!canBulkPriority) return null;

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setOpen(true)}
        disabled={selectedCount === 0}
        className="flex items-center gap-2"
        title={selectedCount === 0
          ? 'Select one or more orders to set priority'
          : `Set priority for ${selectedCount} selected order(s)`}
        data-testid="bulk-priority-open-btn"
      >
        <Flag className="h-4 w-4" />
        Set Priority ({selectedCount})
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-md" data-testid="bulk-priority-modal">
          <DialogHeader>
            <DialogTitle>Set Priority for {selectedCount} Order{selectedCount !== 1 ? 's' : ''}</DialogTitle>
            <DialogDescription>
              Mark the selected orders as Normal, Rush, or Urgent. Locked or
              closed orders will be skipped automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="bulk-priority-select">Priority</Label>
              <Select
                value={priority}
                onValueChange={setPriority}
                disabled={submitting}
              >
                <SelectTrigger
                  id="bulk-priority-select"
                  data-testid="bulk-priority-select"
                >
                  <SelectValue placeholder="Choose a priority..." />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((opt) => (
                    <SelectItem
                      key={opt.value}
                      value={opt.value}
                      data-testid={`bulk-priority-option-${opt.value}`}
                    >
                      <span className="mr-2">{opt.icon}</span>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {needsNotes && (
              <div className="space-y-2">
                <Label htmlFor="bulk-priority-notes">
                  Justification <span className="text-red-600">*</span>
                  <span className="text-xs text-muted-foreground ml-2">
                    (shared across all selected orders, minimum 10 characters)
                  </span>
                </Label>
                <textarea
                  id="bulk-priority-notes"
                  className="w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                  placeholder={`Why are these orders being marked as ${priority?.toUpperCase()}?`}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  disabled={submitting}
                  maxLength={500}
                  data-testid="bulk-priority-notes"
                />
                <div className="text-xs text-muted-foreground text-right">
                  {notes.trim().length}/500
                </div>
              </div>
            )}

            {priority === 'normal' && (
              <p className="text-xs text-muted-foreground bg-muted p-2 rounded">
                Existing priority justifications will be preserved as historical
                context.
              </p>
            )}

            {error && (
              <p className="text-sm text-red-600" data-testid="bulk-priority-error">
                {error}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={submitting}
              data-testid="bulk-priority-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleApply}
              disabled={submitting || !priority || (needsNotes && !notesValid)}
              data-testid="bulk-priority-apply"
            >
              {submitting ? 'Applying…' : `Apply to ${selectedCount} Order${selectedCount !== 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BulkPriorityControls;
